/**
 * Submission Triage pagination footer.
 *
 *   Page [1] of 10   Rows per page [10]    «  ‹  1 2 3 … 8 9 10  ›  »
 *
 * Each button is a bordered pill that matches the page input + rows
 * Select height (Atlas size="sm" → h-9). The current page is filled.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronLeftDouble, ChevronRight, ChevronRightDouble } from "@untitledui/icons";
import { Input, Select, cx } from "@tricura/atlas-ui";

interface PaginationFooterProps {
    total: number;
    offset: number;
    limit: number;
    onChange: (nextOffset: number) => void;
    onLimitChange?: (nextLimit: number) => void;
    pageSizeOptions?: number[];
    label?: string;
    isDisabled?: boolean;
}

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

function buildPageWindow(current: number, total: number): (number | "…")[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const window: (number | "…")[] = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) window.push("…");
    for (let i = start; i <= end; i++) window.push(i);
    if (end < total - 1) window.push("…");
    window.push(total);
    return window;
}

export function PaginationFooter({
    total,
    offset,
    limit,
    onChange,
    onLimitChange,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    isDisabled,
}: PaginationFooterProps) {
    const safeTotal = Math.max(0, total);
    const safeLimit = Math.max(1, limit);
    const pageCount = Math.max(1, Math.ceil(safeTotal / safeLimit));
    const currentPage = Math.min(pageCount, Math.floor(offset / safeLimit) + 1);

    const [pageDraft, setPageDraft] = useState(String(currentPage));
    useEffect(() => {
        setPageDraft(String(currentPage));
    }, [currentPage]);

    const goToPage = (n: number) => {
        const clamped = Math.min(Math.max(1, n), pageCount);
        onChange((clamped - 1) * safeLimit);
    };

    const commitDraft = () => {
        const n = Number(pageDraft);
        if (Number.isFinite(n)) goToPage(Math.round(n));
        else setPageDraft(String(currentPage));
    };

    const window = buildPageWindow(currentPage, pageCount);
    const pageSizeItems = pageSizeOptions.map((n) => ({ id: String(n), label: String(n) }));

    return (
        <div className="border-secondary bg-primary border-t px-4 py-3 md:px-6">
            {/* ---------- Desktop (≥ md) ---------- */}
            <div className="hidden flex-wrap items-center justify-between gap-4 md:flex">
                <div className="flex items-center gap-4">
                    <label className="text-tertiary flex items-center gap-2 text-sm">
                        Page
                        <Input
                            size="sm"
                            aria-label="Page number"
                            data-autotest="triage-pagination-input-page-number"
                            inputMode="numeric"
                            className="w-16 text-center"
                            value={pageDraft}
                            onChange={(value) => setPageDraft(value.replace(/\D/g, ""))}
                            onBlur={commitDraft}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    commitDraft();
                                }
                            }}
                            isDisabled={isDisabled}
                        />
                        of <span className="text-primary tabular-nums">{pageCount}</span>
                    </label>

                    {onLimitChange && (
                        <div className="text-tertiary flex items-center gap-2 text-sm">
                            Rows per page
                            <Select
                                size="sm"
                                aria-label="Rows per page"
                                className="w-20"
                                data-autotest="triage-pagination-select-rows-per-page"
                                selectedKey={String(safeLimit)}
                                items={pageSizeItems}
                                onSelectionChange={(key) => {
                                    if (key === null) return;
                                    const next = Number(key);
                                    if (Number.isFinite(next)) {
                                        onChange(0);
                                        onLimitChange(next);
                                    }
                                }}
                            >
                                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                            </Select>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <ArrowBtn
                        icon={ChevronLeftDouble}
                        ariaLabel="First page"
                        onClick={() => goToPage(1)}
                        disabled={isDisabled || currentPage <= 1}
                        data-autotest="triage-pagination-button-first"
                    />
                    <ArrowBtn
                        icon={ChevronLeft}
                        ariaLabel="Previous page"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={isDisabled || currentPage <= 1}
                        data-autotest="triage-pagination-button-prev"
                    />

                    {window.map((p, i) =>
                        p === "…" ? (
                            <span key={`gap-${i}`} className="text-tertiary px-1 text-sm tabular-nums">
                                …
                            </span>
                        ) : (
                            <PageBtn key={p} page={p} active={p === currentPage}  onClick={() => goToPage(p)} disabled={isDisabled} dataAutotest={`triage-pagination-button-page-${p}`}/>
                        ),
                    )}

                    <ArrowBtn
                        icon={ChevronRight}
                        ariaLabel="Next page"
                        onClick={() => goToPage(currentPage + 1)}
                        data-autotest="triage-pagination-button-next"
                        disabled={isDisabled || currentPage >= pageCount}
                    />
                    <ArrowBtn
                        icon={ChevronRightDouble}
                        ariaLabel="Last page"
                        onClick={() => goToPage(pageCount)}
                        data-autotest="triage-pagination-button-last"
                        disabled={isDisabled || currentPage >= pageCount}
                    />
                </div>
            </div>

            {/* ---------- Mobile (< md) ---------- */}
            <div className="flex items-center justify-between gap-2 md:hidden">
                <div className="flex items-center gap-2">
                    <ArrowBtn
                        icon={ChevronLeftDouble}
                        ariaLabel="First page"
                        onClick={() => goToPage(1)}
                        disabled={isDisabled || currentPage <= 1}
                        data-autotest="pagination-first-page-button"
                    />
                    <ArrowBtn
                        icon={ChevronLeft}
                        ariaLabel="Previous page"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={isDisabled || currentPage <= 1}
                        data-autotest="pagination-prev-page-button"
                    />
                </div>
                <span className="text-tertiary text-sm tabular-nums">
                    Page <span className="text-primary font-semibold">{currentPage}</span> of{" "}
                    <span className="text-primary font-semibold">{pageCount}</span>
                </span>
                <div className="flex items-center gap-2">
                    <ArrowBtn
                        icon={ChevronRight}
                        ariaLabel="Next page"
                        onClick={() => goToPage(currentPage + 1)}
                        data-autotest="pagination-next-page-button"
                        disabled={isDisabled || currentPage >= pageCount}
                    />
                    <ArrowBtn
                        icon={ChevronRightDouble}
                        ariaLabel="Last page"
                        onClick={() => goToPage(pageCount)}
                        data-autotest="pagination-last-page-button"
                        disabled={isDisabled || currentPage >= pageCount}
                    />
                </div>
            </div>
        </div>
    );
}

const PILL_BASE =
    "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border bg-primary px-2 text-sm tabular-nums shadow-xs transition duration-100 ease-linear disabled:cursor-not-allowed disabled:opacity-50";

function PageBtn({ page, active, onClick, disabled, dataAutotest }: { page: number; active: boolean; onClick: () => void; disabled?: boolean; dataAutotest?: string }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-current={active ? "page" : undefined}
            data-autotest={dataAutotest}

            className={cx(
                PILL_BASE,
                active
                    ? "border-primary bg-secondary text-primary font-semibold"
                    : "border-primary text-tertiary hover:bg-secondary hover:text-primary",
            )}
        >
            {page}
        </button>
    );
}

function ArrowBtn({
    icon: Icon,
    ariaLabel,
    onClick,
    disabled,
    "data-autotest": dataAutotest,
}: {
    icon: typeof ChevronLeft;
    ariaLabel: string;
    onClick: () => void;
    disabled?: boolean;
    "data-autotest"?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={ariaLabel}
            data-autotest={dataAutotest}
            className={cx(PILL_BASE, "border-primary text-tertiary hover:bg-secondary hover:text-primary")}
        >
            <Icon aria-hidden="true" className="size-4 stroke-[2.25px]" />
        </button>
    );
}
